/**
 * Pure helpers for the MDF Validation Summary card on the reviewer detail page.
 *
 * MDF's make_decision persists the per-check results as a JSON STRING in
 * `validation_results_json` ({ per_check_results: [ {validation, status,
 * reject_code, note, needs_review_reason, needs_review_detail}, ... ],
 * decision, rejection_code }) — it does NOT persist the `validation_summary`
 * or `key_findings` fields MAP/WMP write. So the detail page fell through to a
 * hardcoded "0/0 passed" and showed no reason on a reject. These helpers derive
 * the count and the blocking-reject explanation from the data already on the
 * row (the same source getMdfChecks() parses for the Claims tab) — no agent
 * change required.
 *
 * Kept as pure functions (no React/cloudscape) so they're unit-testable; the
 * package has no @testing-library/react wired, so DOM-render tests aren't
 * runnable here (see the other MDF frontend helpers).
 */

// SOP step order, used only to pick the FIRST (lowest-numbered) blocking step
// as "the" reason — mirrors make_decision._aggregate, where the lowest failing
// step wins.
const MDF_STEP_ORDER = [
  'step_0_zero_claim_screen',
  'step_1_claim_amount_positive',
  'step_2_po_4j_approval',
  'step_3_invoice_present',
  'step_4_entity_linkage',
  'step_5_cost_share',
  'step_6_line_item_eligibility',
  'step_7_duplicate_invoice',
  'step_8_sponsorship_eligibility',
];

/**
 * Parse `validation_results_json` (string or already-parsed) into the ordered
 * array of per-check result objects. Returns [] on any parse failure so callers
 * degrade gracefully rather than throw.
 * @param {object} request the funding-request row
 * @returns {Array<object>}
 */
export function getMdfPerCheck(request) {
  let parsed = request?.validation_results_json;
  try {
    if (typeof parsed === 'string') parsed = JSON.parse(parsed);
  } catch (e) {
    return [];
  }
  if (Array.isArray(parsed)) return parsed;
  return (parsed && parsed.per_check_results) || [];
}

/**
 * Count the MDF checks that actually ran, by outcome. A step that never ran is
 * simply absent from per_check (make_decision logs it but doesn't fabricate a
 * result), so `total` reflects checks that executed — the honest denominator.
 * @param {Array<object>} perCheck
 * @returns {{passed:number, rejected:number, escalated:number, total:number}}
 */
export function countMdfChecks(perCheck) {
  const counts = { passed: 0, rejected: 0, escalated: 0, total: 0 };
  (perCheck || []).forEach((c) => {
    const status = (c && c.status ? String(c.status) : '').toLowerCase();
    if (status === 'pass') counts.passed += 1;
    else if (status === 'reject') counts.rejected += 1;
    // 'needs_review' is a defensive synonym for 'escalate' used elsewhere in the
    // detail page (mdfStatusType maps both to 'warning'); count it the same, or a
    // check carrying it would drop from BOTH escalated AND total, deflating the
    // "N/M passed" denominator this helper exists to make accurate (AutoSDE).
    else if (status === 'escalate' || status === 'needs_review') counts.escalated += 1;
    else return; // 'error'/unknown/no-status: don't count toward the total
    counts.total += 1;
  });
  return counts;
}

/**
 * The primary "N/M passed" line for the Validations Performed field.
 * @param {{passed:number, total:number}} counts
 * @returns {string} e.g. "6/9 passed"
 */
export function formatMdfValidationSummary(counts) {
  const c = counts || { passed: 0, total: 0 };
  return `${c.passed}/${c.total} passed`;
}

/**
 * The secondary breakdown line, only listing non-zero non-pass buckets so a
 * clean approve shows nothing extra. e.g. "1 rejected · 2 need review".
 * @param {{rejected:number, escalated:number}} counts
 * @returns {string} '' when nothing to add
 */
export function formatMdfValidationBreakdown(counts) {
  const c = counts || {};
  const parts = [];
  if (c.rejected) parts.push(`${c.rejected} rejected`);
  if (c.escalated) parts.push(`${c.escalated} need review`);
  return parts.join(' · ');
}

/**
 * The blocking-reject explanation: the lowest-numbered step whose status is
 * 'reject', with its human-readable code + note. make_decision rejects at the
 * first failing gate (lowest step wins), so this matches the decision the
 * reviewer sees. Returns null when there's no rejecting step (approve/escalate).
 * @param {Array<object>} perCheck
 * @returns {{step:string, reject_code:string|null, reject_detail:string|null, note:string|null}|null}
 */
export function getMdfRejectReason(perCheck) {
  const byStep = {};
  (perCheck || []).forEach((c) => {
    if (c && c.validation) byStep[c.validation] = c;
  });
  for (const step of MDF_STEP_ORDER) {
    const c = byStep[step];
    if (c && String(c.status).toLowerCase() === 'reject') {
      return {
        step,
        reject_code: c.reject_code || null,
        // reject_detail is the agent's plain-English "why this was rejected"
        // sentence (added to every reject path). Fall back to the older note /
        // needs_review_detail for rows validated before it was persisted.
        reject_detail: c.reject_detail || c.note || c.needs_review_detail || null,
        note: c.note || c.needs_review_detail || null,
      };
    }
  }
  // Fall back to array order if a rejecting step isn't in the known SOP list.
  const anyReject = (perCheck || []).find(
    (c) => c && String(c.status).toLowerCase() === 'reject'
  );
  if (anyReject) {
    return {
      step: anyReject.validation || '',
      reject_code: anyReject.reject_code || null,
      reject_detail:
        anyReject.reject_detail || anyReject.note
        || anyReject.needs_review_detail || null,
      note: anyReject.note || anyReject.needs_review_detail || null,
    };
  }
  return null;
}
